// AsnListGR.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import CommonDataTable from '../../components/CommonDataTable';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DetailsGR from './DetailsGR';
import { grnTypeLabel, grnTypeCodeFromLabel } from './grnTypeLabel';
import { fetchGRN, fetchGrnByGrnNumber, refreshGrnWms } from '../../services/GR/grn';
import { toast } from '../../components/AppToast';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';

const baseColumns = [
  { field: 'grnNumber', headerName: 'GRN Number', width: 160 },
  { field: 'asnNumber', headerName: 'ASN Number', width: 160 },
  { field: 'owner', headerName: 'Owner', width: 140 },
  { field: 'facilityId', headerName: 'Facility ID', width: 120 },
  { field: 'facilityName', headerName: 'Facility Name', width: 180 },
  { field: 'totalExpectedQty', headerName: 'Total Expected Qty', width: 180 },
  { field: 'totalReceivedQty', headerName: 'Total Received Qty', width: 180 },
  { field: 'totalExpectedWeight', headerName: 'Total Expected Weight', width: 190 },
  { field: 'totalExpectedVolume', headerName: 'Total Expected Volume', width: 190 },
  { field: 'type', headerName: 'Type', width: 120 },
  { field: 'createdDate', headerName: 'Created Date', width: 160 }
];

export default function AsnListGR({
  onOpenAsn,
  initialGrnNumber,
  initialGrnId,
  onBack,
  onCreateClick
}) {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canAddGRN = hasPermission("GoodsReceipt", "ADD");

  const [rows, setRows] = useState([]);
  const [grnData, setGrnData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [detailsAsn, setDetailsAsn] = useState(null);
  const [openedViaDeepLink, setOpenedViaDeepLink] = useState(false);
  const [activeGrnNumber, setActiveGrnNumber] = useState(null);
  const triedRef = useRef(false);

  console.log('AsnListGR initialGrnNumber:', initialGrnNumber);

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleViewGrnDetails = async () => {
    const wantedGrn = String(menuRow?.grnNumber || '').trim();

    if (!wantedGrn) {
      toast.error('GRN number not found for this row');
      closeMenu();
      return;
    }

    try {
      setLoading(true);

      const res = await fetchGrnByGrnNumber(wantedGrn);
      console.log('fetchGrnByGrnNumber (from row) response:', res);

      const rawData = res?.data ?? res;

      const isEmptyObject =
        rawData &&
        typeof rawData === 'object' &&
        !Array.isArray(rawData) &&
        Object.keys(rawData).length === 0;

      const isEmptyArray = Array.isArray(rawData) && rawData.length === 0;

      const isFailure =
        res?.success === false ||
        rawData == null ||
        isEmptyObject ||
        isEmptyArray;

      if (isFailure) {
        const msg =
          res?.message ||
          `No GRN found for GRN Number (${wantedGrn})`;
        toast.error(msg);
        return;
      }

      const payload = rawData;
      const arr = Array.isArray(payload) ? payload : [payload];

      setGrnData(arr);

      const transformed = transformGRNData(arr);
      const first = transformed[0];

      if (first?.asnNumber) {
        setDetailsAsn(first.asnNumber);
      } else if (first?.grnNumber) {
        setDetailsAsn(first.grnNumber);
      } else {
        toast.error('ASN/GRN not found in detailed GRN response');
        return;
      }

      setOpenedViaDeepLink(false);
      setActiveGrnNumber(first.grnNumber || null);
    } catch (err) {
      console.error('fetchGrnByGrnNumber (from row) failed:', err);
      toast.error(
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to load GRN details for this row'
      );
    } finally {
      setLoading(false);
      closeMenu();
    }
  };

  const handleOpenAsnForm = () => {
    if (menuRow?.asnNumber) onOpenAsn?.(String(menuRow.asnNumber).trim());
    closeMenu();
  };

  const actionColumn = {
    field: '__actions',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <IconButton size="small" onClick={(e) => openMenu(e, params.row)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    )
  };

  // 🔤 Column headers translated
  const columns = [
    actionColumn,
    ...baseColumns.map((col) => ({
      ...col,
      headerName: t(`goodsReceipt.table.${col.field}`)  || col.headerName
    }))
  ];

  const transformGRNData = (apiData) => {
    return (apiData || []).map((grn, index) => {
      const header = grn.AsnHeader || grn || {};

      const typeLabel = grnTypeLabel(header['Type'], t);

      return {
        id: header['GRN Number'] || grn._id || `${index}`,
        _id: grn._id,

        grnNumber: header['GRN Number'] || '',
        asnNumber: header['ASN Number'] || '',
        owner: header['Owner'] || '',
        facilityId: header['Facility ID'] || '',
        facilityName: header['Facility Name'] || '',
        totalExpectedQty: header['Total Expected Qty'] ?? 0,
        totalReceivedQty: header['Total Received Qty'] ?? 0,
        totalExpectedWeight: header['Total Expected Weight'] ?? 0,
        totalExpectedVolume: header['Total Expected Volume'] ?? 0,
        type: typeLabel,
        createdDate: header['Created Date']
          ? new Date(header['Created Date']).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })
          : '',
        rawData: grn
      };
    });
  };

  const GRN_FILTER_FIELD_MAP = {
    __actions: null,
    grnNumber: 'AsnHeader.GRN Number',
    asnNumber: 'AsnHeader.ASN Number',
    owner: 'AsnHeader.Owner',
    facilityId: 'AsnHeader.Facility ID',
    facilityName: 'AsnHeader.Facility Name',
    totalExpectedQty: 'AsnHeader.Total Expected Qty',
    totalReceivedQty: 'AsnHeader.Total Received Qty',
    totalExpectedWeight: 'AsnHeader.Total Expected Weight',
    totalExpectedVolume: 'AsnHeader.Total Expected Volume',
    type: 'AsnHeader.Type',
    createdDate: 'AsnHeader.Created Date'
  };

  function mapGrnUiFiltersToApi(filters = []) {
    if (!Array.isArray(filters)) return [];

    return filters
      .map((f) => {
        if (!f || !f.columnId) return null;

        const apiField = GRN_FILTER_FIELD_MAP[f.columnId] ?? f.columnId;
        if (apiField === null) return null;

        return {
          columnId: apiField,
          operator: f.operator,
          // Type is shown as a label but stored as a code (1 / 7) in WMS.
          value: f.columnId === 'type' ? grnTypeCodeFromLabel(f.value, t) : f.value
        };
      })
      .filter(Boolean);
  }

  // Deep link by GRN Number
  useEffect(() => {
    const wanted = String(initialGrnNumber || '').trim();
    if (!wanted || triedRef.current) return;

    (async () => {
      triedRef.current = true;
      try {
        setLoading(true);

        const res = await fetchGrnByGrnNumber(wanted);
        console.log('fetchGrnByGrnNumber response:', res);

        const rawData = res?.data ?? res;

        const isEmptyObject =
          rawData &&
          typeof rawData === 'object' &&
          !Array.isArray(rawData) &&
          Object.keys(rawData).length === 0;

        const isEmptyArray = Array.isArray(rawData) && rawData.length === 0;

        const isFailure =
          res?.success === false ||
          rawData == null ||
          isEmptyObject ||
          isEmptyArray;

        if (isFailure) {
          const msg =
            res?.message ||
            `No GRN found for the given id (${wanted})`;
          toast.error(msg);

          if (typeof onBack === 'function') {
            onBack();
          }
          return;
        }

        const payload = rawData;
        const arr = Array.isArray(payload) ? payload : [payload];

        setGrnData(arr);
        const transformed = transformGRNData(arr);
        setRows(transformed);

        const first = transformed[0];

        if (first?.asnNumber) {
          setDetailsAsn(first.asnNumber);
          setOpenedViaDeepLink(true);
          setActiveGrnNumber(first.grnNumber || null);
        } else {
          toast.error('ASN number not found for this GRN');
        }
      } catch (err) {
        console.error('fetchGrnByGrnNumber failed:', err);
        toast.error(
          typeof err === 'string'
            ? err
            : err?.message || `Failed to open GRN ${wanted}`
        );
        if (typeof onBack === 'function') {
          onBack();
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [initialGrnNumber, onBack]);





  const loadData = useCallback(
    async (pageNo = 0, pageSizeNo = 10, filter = []) => {
      setLoading(true);
      try {
        const apifilter = mapGrnUiFiltersToApi(filter);
        const apiData = await fetchGRN(pageNo, pageSizeNo, apifilter);
        console.log('Fetched GRN data:', apiData);

        const rawList =
          Array.isArray(apiData?.data?.data)
            ? apiData.data.data
            : Array.isArray(apiData?.data)
            ? apiData.data
            : Array.isArray(apiData)
            ? apiData
            : [];

        const total =
          apiData?.data?.meta?.total ??
          apiData?.totalCount ??
          rawList.length;

        setGrnData(rawList);

        const transformed = transformGRNData(rawList);
        setRows(transformed);
        setTotalCount(total);

        try {
          localStorage.setItem('asnListGR', JSON.stringify(transformed));
        } catch {}
      } catch (err) {
        console.error('Error loading GRN data:', err);
        toast.error(
          `Failed to load GRN data: ${err?.message || 'Unknown error'}`
        );
        try {
          const fallback = JSON.parse(
            localStorage.getItem('asnListGR') || '[]'
          );
          if (Array.isArray(fallback) && fallback.length) {
            setRows(fallback);
            toast.info('Loaded cached GRN data');
          } else {
            setRows([]);
          }
        } catch {
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [t] // typeLabel me t use ho raha hai
  );


  const handleSyncWms = useCallback(async () => {
    await refreshGrnWms();
    await loadData(page, pageSize);
  }, [loadData, page, pageSize]);

  // --- Manual refresh handler used by CommonDataTable refresh button ---
  const onManualRefresh = React.useCallback(
    async ({ filters = [], page: p = page ?? 0, pageSize: ps = pageSize ?? 10 } = {}) => {
      try {
        // simply reuse your existing loader
        await loadData(p, ps, filters);
      } catch (err) {
        console.error("Manual refresh failed:", err);
        toast.error(err?.message || "Refresh failed");
      }
    },
    [loadData, page, pageSize]
  );

  useEffect(() => {
    if (initialGrnNumber) return;
    loadData(page, pageSize);
  }, [loadData, initialGrnNumber, page, pageSize]);

  const defaultVisible = [
    '__actions',
    'grnNumber',
    'asnNumber',
    'owner',
    'facilityName',
    'totalExpectedQty',
    'totalReceivedQty',
    'type',
    'createdDate'
  ];

  if (detailsAsn) {
    return (
      <>
        {!openedViaDeepLink && (
          <nav
            aria-label="breadcrumb"
            className="px-3 py-2 border-bottom bg-light mb-2"
          >
            <ol className="breadcrumb mb-0 align-items-center">
              <li className="breadcrumb-item">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-muted"
                  onClick={() => {
                    setDetailsAsn(null);
                    setOpenedViaDeepLink(false);
                    setActiveGrnNumber(null);
                  }}
                >
                  {t("goodsReceipt.breadcrumb.goodsReceipt")}
                </button>
              </li>
              <li
                className="breadcrumb-item active fw-semibold text-primary"
                aria-current="page"
              >
                {t("goodsReceipt.breadcrumb.viewGrnDetails")}
              </li>
            </ol>
          </nav>
        )}

        <DetailsGR
          asnNumber={detailsAsn}
          grnData={grnData}
          onClose={() => {
            setDetailsAsn(null);
            setOpenedViaDeepLink(false);
            setActiveGrnNumber(null);
          }}
        />
      </>
    );
  }

  console.log('all possible columns in the GRN', columns);

  return (
    <>
      {loading && (
        <div className="text-center py-3">
          {t("goodsReceipt.status.loadingGrn")}
        </div>
      )}

      <CommonDataTable
        id="grn-table"
        allPossibleColumns={columns}
        initialVisible={defaultVisible}
        initialRows={rows}
        height={500}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        forItems
        checkboxSelection
        exportImportPath={'grn'}
        sampleFileUrl="/samples/grn_sample.xlsx"
        sampleFileName="grn"
        forInventory
        showSyncWms
        onSyncWms={handleSyncWms}

        // CREATE BUTTON
        showCreateButton={canAddGRN}
        createButtonText="Create GRN"
        createButtonIcon="bi-plus-lg"
        onCreateClick={onCreateClick}

        // onManualRefresh={onManualRefresh}
        linkColumns={{
          asnNumber: {
            entity: 'ASN',
            parseValues: (val) => (val ? [String(val)] : [])
          }
        }}
        onLinkOpen={({ field, value, row }) => {
          console.log('GRN Table ....................... onLinkOpen called:', {
            field,
            value,
            row
          });
          if (field === 'asnNumber' && value) {
            onOpenAsn?.(value);
          }
        }}
        onDataRefresh={async ({
          filters = [],
          page: p = 0,
          pageSize: ps = pageSize
        } = {}) => {
          console.log(
            'Shipping ....................... received onDataRefresh:',
            { filters, page: p, pageSize: ps }
          );
          await loadData(p, ps, filters);
        }}


        
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleViewGrnDetails}>
          <i className="bi bi-eye me-2" />
          {t("goodsReceipt.menu.viewGrnDetails")}
        </MenuItem>
      </Menu>
    </>
  );
}
